import {
	type EventBooking,
	type InterviewMeeting,
	mapEventBooking,
	MeetingCard,
} from '@/features/interview-scheduling/meeting-card'
import { Button, logger, TimeAgo } from '@/ui'
import { ReconnectCalendarBanner } from '@/ui/core/reconnect-calendar-banner'
import { Tooltip } from '@/ui/rapid-ui/tooltip'
import {
	candidateEventIndexQueryKey,
	type CandidateEventResponse,
	type CandidateEventResponseType,
	candidateEventResponseType,
	pipelineStageShowQueryKey,
	useCampaignAvailabilityCheckShow,
	useCampaignInterviewSettingsShow,
	useCandidateEventIndex,
	useRecruiterBookingAttendance,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { format } from 'date-fns'
import {
	AlertTriangleIcon,
	CalendarCheckIcon,
	CalendarPlusIcon,
	CalendarXIcon,
	CheckIcon,
	ClockIcon,
	CopyIcon,
	FilePlus2Icon,
	type LucideIcon,
	MailIcon,
	MessageCircleIcon,
	RefreshCwIcon,
	SendIcon,
} from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const DEFAULT_BOOKING_WINDOW_DAYS = 14

export interface InterviewsTabProps {
	candidateId: string
	campaignId: string
	onSendBookingLink: () => void
	onJoinInApp?: () => void
}

interface EventPresentation {
	Icon: LucideIcon
	muted: boolean
	accentClassName: string
}

const eventPresentation: Partial<Record<CandidateEventResponseType, EventPresentation>> = {
	[candidateEventResponseType.invitation_created]: {
		Icon: FilePlus2Icon,
		muted: false,
		accentClassName: 'text-grey-500',
	},
	[candidateEventResponseType.invitation_sent]: {
		Icon: SendIcon,
		muted: false,
		accentClassName: 'text-violet-600',
	},
	[candidateEventResponseType.invitation_superseded]: {
		Icon: RefreshCwIcon,
		muted: true,
		accentClassName: 'text-grey-400',
	},
	[candidateEventResponseType.invitation_expired]: {
		Icon: ClockIcon,
		muted: true,
		accentClassName: 'text-amber-600',
	},
	[candidateEventResponseType.booking_confirmed]: {
		Icon: CalendarCheckIcon,
		muted: false,
		accentClassName: 'text-green-600',
	},
	[candidateEventResponseType.booking_cancelled]: {
		Icon: CalendarXIcon,
		muted: true,
		accentClassName: 'text-amber-600',
	},
}

export function InterviewsTab({ candidateId, campaignId, onSendBookingLink, onJoinInApp }: InterviewsTabProps) {
	const { t } = useTranslation('common')
	const { companyId } = useParams({ from: '/_auth/company/$companyId/$campaignId/candidates/$candidateId' })
	const queryClient = useQueryClient()
	const [attendanceError, setAttendanceError] = useState<string | null>(null)

	const { data: availability, isPending: availabilityPending } = useCampaignAvailabilityCheckShow(campaignId)
	const { data: settings } = useCampaignInterviewSettingsShow(campaignId)
	const { data: eventIndex, isPending: eventsPending } = useCandidateEventIndex(candidateId)
	// This tab surfaces only interview-lifecycle events; other candidate activity
	// (messages, stage moves, applications) shares the endpoint but isn't shown here.
	const events = eventIndex?.events?.filter((event) => eventPresentation[event.type] != null)

	const setAttendance = useRecruiterBookingAttendance({
		onSuccess: (_data, variables) => {
			setAttendanceError(null)
			logger.track('interviews:set_attendance', {
				data: {
					candidate_id: candidateId,
					campaign_id: campaignId,
					booking_id: variables.bookingId,
					attended: variables.data.attended,
				},
			})
			// The recruiter just recorded attendance on this exact candidate — flip the
			// board badge and the timeline card now rather than waiting for the poll.
			void queryClient.invalidateQueries({ queryKey: pipelineStageShowQueryKey(campaignId) })
			void queryClient.invalidateQueries({ queryKey: candidateEventIndexQueryKey(candidateId) })
		},
		onError: (error, variables) => {
			logger.error(error, {
				tags: { component: 'InterviewsTab', action: 'setAttendance' },
				data: { candidateId, campaignId, bookingId: variables.bookingId },
			})
			setAttendanceError(t('interviews.attendance.error'))
		},
	})

	function handleSetAttendance(booking: EventBooking, attended: boolean) {
		setAttendanceError(null)
		setAttendance.mutate({ bookingId: booking.id, data: { attended } })
	}

	if (availabilityPending) {
		return <div className="text-sm text-grey-400">{t('interviews.loading')}</div>
	}

	const windowDays = settings?.interviewBookingWindowDays ?? DEFAULT_BOOKING_WINDOW_DAYS
	// Pending is handled above, so a null availability here means the lookup failed.
	// We can't tell whether sending is possible, so block it (and hint a retry)
	// rather than letting an invite fail on submit.
	const availabilityUnavailable = availability == null
	// The recruiter's own availability isn't set up. The candidate's interview
	// history stays visible (it's org-wide, not personal) — we only block sending
	// a new invite, which the backend rejects without configured availability too.
	const recruiterNotConfigured = availability != null && !availability.configured
	const campaignNotConfigured = availability != null && availability.configured && !availability.campaignConfigured
	const settingsIncomplete = availabilityUnavailable
		|| recruiterNotConfigured
		|| (settings != null && settings.interviewDurationMinutes == null)
		|| campaignNotConfigured

	let sendDisabledTooltip = t('interviews.campaignIncomplete.tooltip')
	if (availabilityUnavailable) {
		sendDisabledTooltip = t('interviews.availabilityError.tooltip')
	} else if (recruiterNotConfigured) {
		sendDisabledTooltip = t('interviews.noAvailability.tooltip')
	}
	const hasNoSlots = availability != null && availability.configured && availability.campaignConfigured
		&& !availability.hasSlotsInWindow
	const hasEvents = events != null && events.length > 0
	const now = new Date()

	return (
		<div className="space-y-4">
			<ReconnectCalendarBanner />
			{availabilityUnavailable
				? (
					<div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
						<AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
						<div className="flex-1">{t('interviews.availabilityError.warning')}</div>
					</div>
				)
				: null}
			{recruiterNotConfigured
				? (
					<div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
						<AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
						<div className="flex-1">
							{t('interviews.noAvailability.warning')}{' '}
							<button
								type="button"
								onClick={() => window.open('/account/availability', '_blank')}
								className="font-medium underline"
							>
								{t('interviews.noAvailability.cta')}
							</button>
						</div>
					</div>
				)
				: null}
			{campaignNotConfigured
				? (
					<div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
						<AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
						<div className="flex-1">
							{t('interviews.campaignNotConfigured.warning')}{' '}
							<button
								type="button"
								onClick={() =>
									window.open(`/company/${companyId}/${campaignId}/settings/interview`, '_blank')}
								className="font-medium underline"
							>
								{t('interviews.campaignNotConfigured.cta')}
							</button>
						</div>
					</div>
				)
				: null}
			{hasNoSlots
				? (
					<div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
						<AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
						<div className="flex-1">
							{t('interviews.noSlots.warning', { count: windowDays })}{' '}
							<button
								type="button"
								onClick={() => window.open('/account/availability', '_blank')}
								className="font-medium underline"
							>
								{t('interviews.noSlots.adjust')}
							</button>
						</div>
					</div>
				)
				: null}

			{attendanceError
				? (
					<div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800">
						<AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
						<div className="flex-1">{attendanceError}</div>
					</div>
				)
				: null}

			<ol className="relative">
				<TimelineRow
					showConnector={eventsPending || hasEvents}
					marker={
						<CalendarPlusIcon
							className={`size-3.5 ${settingsIncomplete ? 'text-grey-300' : 'text-violet-600'}`}
						/>
					}
				>
					<Tooltip
						content={sendDisabledTooltip}
						disabled={!settingsIncomplete}
						wrapperClassName="inline-flex"
					>
						<Button
							type="committal"
							size="short"
							onClick={onSendBookingLink}
							disabled={settingsIncomplete}
						>
							{t('interviews.sendBookingLink')}
						</Button>
					</Tooltip>
				</TimelineRow>

				{eventsPending
					? (
						<TimelineRow
							showConnector={false}
							marker={<span className="size-2 rounded-full bg-grey-200" />}
						>
							<span className="text-sm leading-5 text-grey-400">{t('interviews.loading')}</span>
						</TimelineRow>
					)
					: events?.map((event, index) => {
						// Each "interview scheduled" event resolves its own booking by id, so
						// every one renders its card — not just the candidate's most recent one.
						const booking = event.booking ? mapEventBooking(event.booking) : null
						const meeting = booking?.meeting ?? null
						// Attendance can be recorded once a confirmed interview's time has passed;
						// the two-way control stays editable so a recruiter can correct it.
						const showAttendance = booking?.status === 'confirmed' && booking.meeting.startsAt < now

						return (
							<EventRow
								key={event.id}
								event={event}
								showConnector={index < events.length - 1}
								showAttendance={showAttendance}
								candidateAttended={booking?.candidateAttended ?? null}
								isSavingAttendance={setAttendance.isPending}
								onSetAttendance={(attended) => {
									if (booking) {
										handleSetAttendance(booking, attended)
									}
								}}
								meeting={meeting}
								onJoinInApp={onJoinInApp}
							/>
						)
					})}
			</ol>
		</div>
	)
}

function TimelineRow(
	{ marker, showConnector, children }: { marker: ReactNode; showConnector: boolean; children: ReactNode },
) {
	return (
		<li className="flex gap-3">
			<div className="relative flex w-4 flex-col items-center">
				{showConnector
					? (
						<span
							aria-hidden={true}
							className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-grey-200"
						/>
					)
					: null}
				<span className="relative z-10 flex h-5 w-4 items-center justify-center bg-white">{marker}</span>
			</div>
			<div className={`flex-1 ${showConnector ? 'pb-5' : ''}`}>{children}</div>
		</li>
	)
}

function EventRow({
	event,
	showConnector,
	showAttendance = false,
	candidateAttended = null,
	isSavingAttendance = false,
	onSetAttendance,
	meeting = null,
	onJoinInApp,
}: {
	event: CandidateEventResponse
	showConnector: boolean
	showAttendance?: boolean
	candidateAttended?: boolean | null
	isSavingAttendance?: boolean
	onSetAttendance?: (attended: boolean) => void
	meeting?: InterviewMeeting | null
	onJoinInApp?: () => void
}) {
	const { t } = useTranslation('common')
	const [copied, setCopied] = useState(false)
	const occurredAt = new Date(event.occurredAt)

	// The row never unmounts on its own, so clear the success state on a timer instead of
	// leaving it stuck on "Copied"; cleanup guards against a re-copy or unmount mid-timeout.
	useEffect(() => {
		if (!copied) {
			return
		}
		const timeout = setTimeout(() => setCopied(false), 2000)
		return () => clearTimeout(timeout)
	}, [copied])

	// New event types can ship server-side before this frontend knows them; skip rather than
	// crash the whole tab on an unmapped type.
	const presentation: EventPresentation | undefined = eventPresentation[event.type]
	if (!presentation) {
		return null
	}
	const { Icon, muted, accentClassName } = presentation
	const channels = Array.isArray(event.context.channels) ? (event.context.channels as string[]) : []
	const scheduledAt = typeof event.context.scheduledAt === 'string'
		? new Date(event.context.scheduledAt)
		: null
	// For an invitation the server reports the token's current status (and, while it is still
	// active, a bookingUrl). Surfacing the status on every invitation row makes it clear why an
	// older one has no copy link — it was superseded, booked, expired or cancelled.
	const invitationStatus = event.type === candidateEventResponseType.invitation_created
			&& typeof event.context.tokenStatus === 'string'
		? event.context.tokenStatus
		: null
	const bookingUrl = typeof event.context.bookingUrl === 'string' ? event.context.bookingUrl : null

	async function copyBookingUrl() {
		if (!bookingUrl) {
			return
		}
		try {
			await navigator.clipboard.writeText(bookingUrl)
			setCopied(true)
		} catch (error) {
			logger.error(error, { tags: { component: 'InterviewsTab', action: 'copyBookingUrl' } })
		}
	}

	return (
		<TimelineRow showConnector={showConnector} marker={<Icon className={`size-3.5 ${accentClassName}`} />}>
			<div className="flex items-center gap-1.5">
				<span className={`text-sm leading-5 ${muted ? 'text-grey-400' : 'text-grey-700'}`}>
					{t(`interviews.timeline.event.${event.type}`)}
				</span>
				{channels.includes('email')
					? (
						<Tooltip content={t('sendBookingLinkDialog.channel.email')} wrapperClassName="inline-flex">
							<MailIcon className="size-3.5 text-grey-400" />
						</Tooltip>
					)
					: null}
				{channels.includes('whatsapp')
					? (
						<Tooltip content={t('sendBookingLinkDialog.channel.whatsapp')} wrapperClassName="inline-flex">
							<MessageCircleIcon className="size-3.5 text-grey-400" />
						</Tooltip>
					)
					: null}
			</div>
			<span title={format(occurredAt, 'PPpp')} className="mt-0.5 block text-xs text-grey-400">
				{event.type === candidateEventResponseType.booking_confirmed
					? `${t('interviews.timeline.booked')} `
					: null}
				<TimeAgo date={occurredAt} ago={true} />
			</span>
			{meeting
				? (
					<MeetingCard
						meeting={meeting}
						className="mt-1.5"
						showAttendance={showAttendance}
						candidateAttended={candidateAttended}
						isSavingAttendance={isSavingAttendance}
						onSetAttendance={onSetAttendance}
						onJoinInApp={onJoinInApp}
					/>
				)
				: scheduledAt
				? (
					<span className="block text-xs font-medium text-grey-700">
						{format(scheduledAt, 'PPp')}
					</span>
				)
				: null}
			{invitationStatus
				? (
					<div className="mt-1.5 flex items-center gap-1.5">
						<span className="text-xs font-medium text-grey-500">
							{t(`interviews.timeline.tokenStatus.${invitationStatus}`)}
						</span>
						{bookingUrl
							? (
								<>
									<span aria-hidden="true" className="text-grey-300">·</span>
									<button
										type="button"
										onClick={copyBookingUrl}
										className="flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700"
									>
										{copied
											? <CheckIcon className="size-3.5" />
											: <CopyIcon className="size-3.5" />}
										{copied ? t('interviews.copied') : t('interviews.copyLink')}
									</button>
								</>
							)
							: null}
					</div>
				)
				: null}
		</TimelineRow>
	)
}
